import React, { useState } from 'react';
import './RetroCalendarPopup.css';

function RetroCalendarPopup({ value, onSelect, onClose, rangeMode = false, startValue, endValue }) {
  const selectedDate = value ? new Date(value) : new Date();
  const [viewDate, setViewDate] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

  // For range mode: track which date we're selecting (start or end)
  const [selectingStart, setSelectingStart] = useState(true);

  const monthNames = [
    'JANUAR', 'FEBRUAR', 'MÄRZ', 'APRIL', 'MAI', 'JUNI',
    'JULI', 'AUGUST', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DEZEMBER'
  ];

  const dayNames = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];

  const getDaysInMonth = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Get first day of week (0 = Sunday, 1 = Monday, etc.)
    let startingDayOfWeek = firstDay.getDay();
    // Convert Sunday (0) to 7 for our Monday-first calendar
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handlePreviousMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const handleDayClick = (date) => {
    if (!date) return;

    if (rangeMode) {
      // In range mode, we need to select start and end dates
      if (selectingStart || !startValue) {
        // Select start date
        onSelect(date, 'start');
        setSelectingStart(false);
      } else {
        // Select end date
        // Make sure end date is after start date
        const start = new Date(startValue);
        if (date >= start) {
          onSelect(date, 'end');
          setSelectingStart(true); // Reset for next selection
        } else {
          // If clicked date is before start, make it the new start
          onSelect(date, 'start');
        }
      }
    } else {
      // Single date mode
      onSelect(date);
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;

    if (rangeMode) {
      // In range mode, check if date is start or end
      if (startValue && date.toDateString() === new Date(startValue).toDateString()) return true;
      if (endValue && date.toDateString() === new Date(endValue).toDateString()) return true;
      return false;
    } else {
      // Single date mode
      if (!value) return false;
      const selected = new Date(value);
      return date.toDateString() === selected.toDateString();
    }
  };

  const isInRange = (date) => {
    if (!rangeMode || !date || !startValue || !endValue) return false;
    const start = new Date(startValue);
    const end = new Date(endValue);
    return date > start && date < end;
  };

  const isRangeStart = (date) => {
    if (!rangeMode || !date || !startValue) return false;
    return date.toDateString() === new Date(startValue).toDateString();
  };

  const isRangeEnd = (date) => {
    if (!rangeMode || !date || !endValue) return false;
    return date.toDateString() === new Date(endValue).toDateString();
  };

  const days = getDaysInMonth();

  return (
    <div className="retro-calendar-overlay" onClick={onClose}>
      <div className="retro-calendar-popup" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="retro-calendar-header">
          <button type="button" onClick={handlePreviousMonth} className="retro-calendar-nav">
            &lt;
          </button>
          <div className="retro-calendar-title">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </div>
          <button type="button" onClick={handleNextMonth} className="retro-calendar-nav">
            &gt;
          </button>
        </div>

        {/* Quick action */}
        <div className="retro-calendar-quick-actions">
          <button type="button" onClick={handleToday} className="retro-calendar-today-btn">
            HEUTE
          </button>
        </div>

        {/* Weekday headers */}
        <div className="retro-calendar-weekdays">
          {dayNames.map(day => (
            <div key={day} className="retro-calendar-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="retro-calendar-days">
          {days.map((date, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(date)}
              disabled={!date}
              className={`retro-calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''} ${isSelected(date) ? 'selected' : ''} ${isInRange(date) ? 'in-range' : ''} ${isRangeStart(date) ? 'range-start' : ''} ${isRangeEnd(date) ? 'range-end' : ''}`}
            >
              {date ? date.getDate() : ''}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="retro-calendar-footer">
          <button type="button" onClick={onClose} className="retro-calendar-close-btn">
            SCHLIEßEN
          </button>
        </div>
      </div>
    </div>
  );
}

export default RetroCalendarPopup;
